import { WeibullDataPoint, WeibullParameters, WeibullAnalysis } from '../types/analytics.types';

export const calculateWeibullAnalysis = (
    dataPoints: WeibullDataPoint[],
    title: string,
    productName: string,
    testCondition: string,
    confidenceLevel: number
): Partial<WeibullAnalysis> | null => {
    if (dataPoints.length < 3) {
        return null;
    }

    // Sort data points by failure time
    const sortedData = [...dataPoints]
        .filter(p => p.censoringStatus === 'Failed')
        .sort((a, b) => a.failureTime - b.failureTime);

    // Calculate Median Ranks
    const n = sortedData.reduce((sum, p) => sum + p.quantity, 0);
    let cumulativeRank = 0;
    const rankedData = sortedData.map((point) => {
        const rank = cumulativeRank + point.quantity;
        const medianRank = (rank - 0.3) / (n + 0.4);
        cumulativeRank = rank;
        return {
            time: point.failureTime,
            medianRank,
            lnTime: Math.log(point.failureTime),
            lnLnReliability: Math.log(-Math.log(1 - medianRank)),
        };
    });

    // Linear regression for Weibull parameters
    const sumX = rankedData.reduce((sum, d) => sum + d.lnTime, 0);
    const sumY = rankedData.reduce((sum, d) => sum + d.lnLnReliability, 0);
    const sumXY = rankedData.reduce((sum, d) => sum + d.lnTime * d.lnLnReliability, 0);
    const sumX2 = rankedData.reduce((sum, d) => sum + d.lnTime ** 2, 0);

    const n_points = rankedData.length;
    const beta = (n_points * sumXY - sumX * sumY) / (n_points * sumX2 - sumX ** 2);
    const intercept = (sumY - beta * sumX) / n_points;
    const eta = Math.exp(-intercept / beta);

    // Calculate R-squared
    const yMean = sumY / n_points;
    const ssTotal = rankedData.reduce((sum, d) => sum + (d.lnLnReliability - yMean) ** 2, 0);
    const ssResidual = rankedData.reduce((sum, d) => {
        const predicted = beta * d.lnTime + intercept;
        return sum + (d.lnLnReliability - predicted) ** 2;
    }, 0);
    const rSquared = 1 - ssResidual / ssTotal;

    // Calculate life predictions (BX life)
    const calculateBXLife = (x: number) => {
        return eta * Math.pow(-Math.log(1 - x / 100), 1 / beta);
    };

    const b10Life = calculateBXLife(10);
    const b50Life = calculateBXLife(50);
    const b90Life = calculateBXLife(90);

    // Calculate mean life (MTTF) for Weibull distribution
    const meanLife = eta * Math.exp(Math.log(1 + 1 / beta));

    // Confidence bounds (simplified - normally use Fisher Matrix)
    const stdErrorBeta = beta * 0.2; // Simplified
    const zValue = confidenceLevel === 0.90 ? 1.645 : 1.96; // 90% or 95%

    const parameters: WeibullParameters = {
        beta,
        eta,
        rSquared,
    };

    return {
        title,
        productName,
        testCondition,
        dataPoints,
        parameters,
        b10Life,
        b50Life,
        b90Life,
        meanLife,
        confidenceLevel,
        lowerBoundBeta: beta - zValue * stdErrorBeta,
        upperBoundBeta: beta + zValue * stdErrorBeta,
        lowerBoundEta: eta * 0.8, // Simplified
        upperBoundEta: eta * 1.2, // Simplified
    };
};

export const interpretBeta = (beta: number): string => {
    if (beta < 1) return 'Infant mortality (decreasing failure rate)';
    if (beta === 1) return 'Random failures (constant failure rate - Exponential)';
    if (beta > 1 && beta < 3) return 'Early wear-out';
    if (beta >= 3 && beta < 4) return 'Normal wear-out';
    return 'Rapid wear-out';
};
